import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import FormControl from '../../common/FormControl';
import Toast, { ToastMessage } from '../../common/Toast';
import DualListSelector, { DualListItem } from './common/DualListSelector';
import { ProcedureTemplate } from '../../../services/auditService';
import { ProcedureConfigHandle } from './ProcedureRiskAssessment';
import controlTestingService, { TemplateControl } from '../../../services/controlTestingService';
import controlRegisterService, { ControlRegister } from '../../../services/controlRegisterService';
import extractApiError from '../../../utils/apiError';

interface Props {
    procedure: ProcedureTemplate;
    /** Reports save/loading state up so the host footer button can disable itself. */
    onStatusChange?: (status: { saving: boolean; loading: boolean }) => void;
}

// After migration 066 the audit procedure row IS the Control Test template — same
// physical row in ADT_procedure_templates. So we configure scope (controls covered),
// cadence, and due-date rule directly via the canonical CTR endpoints. No more
// "link to a separate template" indirection.
// Save is driven from the host modal footer (mirrors the CAT template config) via
// the imperative ProcedureConfigHandle exposed on the ref.
const ProcedureControlTest = forwardRef<ProcedureConfigHandle, Props>(({ procedure, onStatusChange }, ref) => {
    const [cadence, setCadence] = useState<string>('');
    const [dueRule, setDueRule] = useState<string>('');
    const [allControls, setAllControls] = useState<ControlRegister[]>([]);
    const [selected, setSelected] = useState<DualListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const initialCadenceRef = React.useRef<string>('');
    const initialDueRuleRef = React.useRef<string>('');

    // Approved templates are frozen: no field edits, no scope changes. Mirror the
    // CAT template config — inputs/scope disabled, save blocked, button locked.
    // Reopen via the status dot to edit.
    const isApproved = procedure.workflow_state === 'approved';

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            controlTestingService.get(procedure.id),
            controlRegisterService.getAll(),
            controlTestingService.listControls(procedure.id),
        ]).then(([tpl, controls, scope]) => {
            if (cancelled) return;
            const c = tpl.cadence || '';
            const d = tpl.due_date_definition || '';
            setCadence(c);
            setDueRule(d);
            initialCadenceRef.current = c;
            initialDueRuleRef.current = d;
            // Only approved controls belong in the scope picker — getAll() returns
            // every workflow state (draft/submitted/rejected leaked in otherwise).
            setAllControls(controls.filter((c: ControlRegister) => c.workflow_state === 'approved'));
            setSelected(scope.map((s: TemplateControl) => ({
                id: s.id,
                title: s.control_code,
                subtitle: s.control_type,
                description: s.control_name,
            })));
        }).catch((e: any) => {
            if (!cancelled) setToast({ type: 'error', message: extractApiError(e, 'Failed to load configuration.') });
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [procedure.id]);

    const items: DualListItem[] = allControls.map(c => ({
        id: c.id,
        title: c.control_code,
        subtitle: c.control_type,
        description: c.control_name,
        category: c.category_name,
    }));

    const handleSave = useCallback(async () => {
        // Approved templates are frozen — block the save and surface why via a
        // toast, mirroring the CAT template config's approved case.
        if (isApproved) {
            setToast({ type: 'error', message: 'Record is approved and locked. Reopen via the workflow button to edit.' });
            return;
        }
        setSaving(true);
        try {
            if (cadence !== initialCadenceRef.current || dueRule !== initialDueRuleRef.current) {
                await controlTestingService.update(procedure.id, {
                    cadence: cadence || undefined,
                    due_date_definition: dueRule || undefined,
                });
                initialCadenceRef.current = cadence;
                initialDueRuleRef.current = dueRule;
            }
            await controlTestingService.replaceScope(procedure.id, selected.map(s => Number(s.id)));
            setToast({ type: 'success', message: 'Configuration saved.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save configuration.') });
        } finally {
            setSaving(false);
        }
    }, [cadence, dueRule, selected, procedure.id, isApproved]);

    // Expose save() to the host modal footer + report status for its disabled state.
    useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);
    useEffect(() => { onStatusChange?.({ saving, loading }); }, [saving, loading, onStatusChange]);

    return (
        <>
            <div className="flex flex-col gap-y-4 pt-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <FormControl.Input
                        label="Cadence"
                        data-testid="INP_control_test_template_cadence"
                        value={cadence}
                        onChange={e => setCadence(e.target.value)}
                        disabled={isApproved}
                        placeholder="e.g. Quarterly"
                    />
                    <FormControl.Input
                        label="Due Date Rule"
                        data-testid="INP_control_test_template_due_rule"
                        value={dueRule}
                        onChange={e => setDueRule(e.target.value)}
                        disabled={isApproved}
                        placeholder="e.g. Last day of quarter"
                    />
                </div>

                <p className="text-sm font-semibold text-gray-700">Scope — Controls covered</p>
                <div style={{ height: 340 }}>
                    {loading ? (
                        <div className="h-full flex items-center justify-center text-gray-500">Loading…</div>
                    ) : (
                        <DualListSelector
                            availableItems={items}
                            selectedItems={selected}
                            onSelectionChange={setSelected}
                            disabled={isApproved}
                            titleLeft="Available Controls"
                            titleRight="Controls in Template"
                            searchPlaceholder="Search controls…"
                            emptyMessageLeft="No more controls available"
                            emptyMessageRight="No controls selected"
                        />
                    )}
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
});

ProcedureControlTest.displayName = 'ProcedureControlTest';

export default ProcedureControlTest;
